import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getPledgeById, updatePledge, deletePledge as apiDeletePledge, addCheckin, deleteCheckin } from '../services/pledgeService';
import { getUsers, getCharities } from '../services/dataService';
import { getCachedData } from '../services/cacheService';
// Assuming check-ins are part of the pledge object or fetched separately
// For simplicity, let's assume pledge.checkins is an array
import LoadingSpinner from './common/LoadingSpinner';
import ConfirmModal from './common/ConfirmModal';
import ProgressBar from './common/ProgressBar';

// --- Pledge-window helpers -------------------------------------------------
// These mirror the backend's single source of truth
// (functions/pledges/utils/completion.py: week_index_for / count_completed_weeks)
// so the admin view agrees with what settlement actually honors. A check-in is
// bucketed by its LOCAL calendar date in the PLEDGE'S timezone, and only weeks
// whose check-in count reaches visits_per_week count toward completion.

// Local YYYY-MM-DD of an instant, evaluated in the given IANA timezone.
const localDateInTz = (instant, timeZone) => {
  const d = new Date(instant);
  if (isNaN(d.getTime())) return null;
  try {
    // en-CA formats as YYYY-MM-DD.
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timeZone || 'UTC',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch (e) {
    return d.toISOString().split('T')[0];
  }
};

// 0-based pledge week index of a check-in, in the pledge's timezone.
// Returns null if it can't be computed. Negative => before the pledge started.
const weekIndexFor = (startDate, timeZone, checkin) => {
  if (!startDate) return null;
  const localStr = localDateInTz(checkin, timeZone);
  if (!localStr) return null;
  // Compare as UTC-midnight dates so DST never shifts the day delta.
  const start = new Date(`${String(startDate).split('T')[0]}T00:00:00Z`);
  const local = new Date(`${localStr}T00:00:00Z`);
  if (isNaN(start.getTime()) || isNaN(local.getTime())) return null;
  const diffDays = Math.floor((local - start) / (24 * 60 * 60 * 1000));
  return Math.floor(diffDays / 7);
};

// Per-week in-window check-in counts (keyed by week index). Out-of-window
// check-ins (before start or beyond duration) are excluded — exactly as
// settlement's count_completed_weeks does.
const inWindowWeekCounts = (pledge) => {
  const durationWeeks = Number(pledge.duration_weeks) || 0;
  const counts = {};
  (pledge.checkins || []).forEach((c) => {
    const idx = weekIndexFor(pledge.start_date, pledge.time_zone, c);
    if (idx !== null && idx >= 0 && idx < durationWeeks) {
      counts[idx] = (counts[idx] || 0) + 1;
    }
  });
  return counts;
};

const PledgeView = () => {
  const { pledgeId } = useParams();
  const navigate = useNavigate();
  const [pledge, setPledge] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    amount_cents: 0,
    duration_weeks: 0,
    visits_per_week: 0,
    start_date: '',
    // Add other editable fields as needed
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [addingCheckin, setAddingCheckin] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [checkinToDelete, setCheckinToDelete] = useState(null);
  const [showCheckinConfirmModal, setShowCheckinConfirmModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState('');

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Load users and charities data to cache for name lookups
        await Promise.all([getUsers(), getCharities()]);
        
        const pledgeData = await getPledgeById(pledgeId);
        if (!pledgeData) {
          setError('Pledge not found.');
          setPledge(null);
        } else {
          setPledge(pledgeData);
          setFormData({
            title: pledgeData.title || '',
            amount_cents: pledgeData.amount_cents || 0,
            duration_weeks: pledgeData.duration_weeks || 0,
            visits_per_week: pledgeData.visits_per_week || 0,
            // start_date arrives as YYYY-MM-DD (or ISO) from the backend;
            // normalize to YYYY-MM-DD for the <input type="date">.
            start_date: pledgeData.start_date ? String(pledgeData.start_date).split('T')[0] : '',
          });
        }
      } catch (err) {
        console.error('Error fetching pledge data:', err);
        setError('Failed to load pledge data. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    if (pledgeId) {
      loadData();
    }
  }, [pledgeId]);

  const handleChange = (e) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'number' ? parseInt(value, 10) : value
    }));
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');
    try {
      // PUT /pledges/{id} accepts pledge metadata fields. We deliberately do
      // NOT send `status` here: under the Stripe-funds-segregation backend,
      // status transitions are driven by settlement (active -> inactive /
      // settlement_failed), not free-form admin edits, and the stale
      // status-transition validator rejects the current enum values.
      const updatePayload = {
        title: formData.title,
        amount_cents: Number(formData.amount_cents),
        duration_weeks: Number(formData.duration_weeks),
        visits_per_week: Number(formData.visits_per_week),
      };

      // Allow the admin to backdate the pledge start_date. This is the fast
      // way to simulate a fulfilled pledge: set start_date in the past so the
      // pledge window has effectively elapsed, then add admin check-ins and
      // settle. We only send start_date — the backend ALWAYS derives end_date
      // from start_date + duration_weeks (inclusive final day) and ignores any
      // client-supplied end_date, so it can never drift from the pledge window
      // the rest of the system computes.
      if (formData.start_date) {
        updatePayload.start_date = formData.start_date;
      }

      const updated = await updatePledge(pledgeId, updatePayload);
      // Reflect the server's returned row when available, else merge locally.
      setPledge(prev => ({ ...prev, ...(updated || updatePayload) }));
      setSuccessMessage('Pledge details updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating pledge:', err);
      const raw = err.message || '';
      let message = 'Failed to update pledge. ' + raw;
      // Admins are allowed to edit any user's pledge (backend update path is
      // admin-or-owner). A 403 here would now indicate a genuine permission
      // problem (e.g. the session's admin flag was revoked), so surface it
      // plainly rather than as a hard "owner-only" rule.
      if (raw.includes('403') || raw.toLowerCase().includes('permission') || raw.toLowerCase().includes('forbidden')) {
        message = 'You do not have permission to edit this pledge.';
      }
      setError(message);
    } finally {
      setSaving(false);
    }
  };

  const handleAddCheckin = async () => {
    setAddingCheckin(true);
    setError(null);
    setSuccessMessage('');
    try {
      // Send a TIMEZONE-NAIVE local datetime (no 'Z', no offset) at noon on
      // the selected date. The backend (process_checkin_time) interprets a
      // naive timestamp in the PLEDGE'S timezone — the single authority for
      // when a pledge's week is — so we must NOT bake in the admin's browser
      // zone here. Noon avoids midnight/DST edges that could shift the local
      // day. (The previous code mixed `new Date("YYYY-MM-DD")` [parsed as UTC
      // midnight] with setHours [local], which could land the check-in on the
      // wrong day.)
      const timestamp = `${selectedDate}T12:00:00`;

      console.log('🔍 ADDING CHECKIN WITH (pledge-local) TIMESTAMP:', timestamp);
      const checkinResponse = await addCheckin(pledgeId, { timestamp });
      console.log('🔍 CHECKIN RESPONSE:', checkinResponse);
      
      // Refresh pledge data to see new checkin
      const updatedPledge = await getPledgeById(pledgeId);
      setPledge(updatedPledge);
      
      setSuccessMessage('Check-in added successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding checkin:', err);
      
      // Show error message in modal popup
      setErrorModalMessage(err.message || 'Failed to add check-in');
      setShowErrorModal(true);
    } finally {
      setAddingCheckin(false);
    }
  };

  const handleDeletePledge = () => {
    setShowConfirmModal(true);
  };

  const handleDeleteCheckin = (checkin) => {
    setCheckinToDelete(checkin);
    setShowCheckinConfirmModal(true);
  };

  const confirmDeleteCheckin = async () => {
    if (checkinToDelete === null) return;
    
    setLoading(true);
    setError(null);
    try {
      // Get the timestamp string - handle both string timestamps and object format
      const timestampValue = typeof checkinToDelete === 'string' ? checkinToDelete : checkinToDelete.timestamp;
      console.log('🗑️ DELETE CHECKIN REQUEST:', { pledgeId, timestampValue, originalCheckin: checkinToDelete });
      await deleteCheckin(pledgeId, timestampValue);
      
      // Refresh pledge data to see updated checkins
      const updatedPledge = await getPledgeById(pledgeId);
      setPledge(updatedPledge);
      
      setSuccessMessage('Check-in deleted successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting checkin:', err);
      setError('Failed to delete check-in. ' + (err.message || ''));
    } finally {
      setLoading(false);
      setShowCheckinConfirmModal(false);
      setCheckinToDelete(null);
    }
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await apiDeletePledge(pledgeId);
      console.log(`Pledge ${pledgeId} deleted.`);
      navigate(-1); // Navigate back (e.g., to user view or home)
    } catch (err) {
      console.error('Error deleting pledge:', err);
      setError('Failed to delete pledge.');
      setLoading(false);
    } finally {
      setShowConfirmModal(false);
    }
  };

  // Helper functions to lookup names from cache
  const getUserNameById = (userId) => {
    const cachedUsers = getCachedData('users');
    if (cachedUsers && cachedUsers.data && cachedUsers.data.users) {
      const user = cachedUsers.data.users.find(u => u.id === userId);
      return user ? user.name : '';
    }
    return '';
  };

  const getCharityNameById = (charityId) => {
    const cachedCharities = getCachedData('charities');
    if (cachedCharities && cachedCharities.data && cachedCharities.data.charities) {
      const charity = cachedCharities.data.charities.find(c => c.id === charityId);
      return charity ? charity.name : '';
    }
    return '';
  };

  if (loading && !pledge) return <div className="flex justify-center items-center h-screen"><LoadingSpinner /></div>;
  if (error && !pledge) return <div className="container mx-auto p-4 text-red-500 text-center">{error}</div>;
  if (!pledge) return <div className="container mx-auto p-4 text-center">Pledge not found.</div>;

  const totalVisitsRequired = pledge.duration_weeks * pledge.visits_per_week;
  // Count only check-ins that fall INSIDE the pledge window, matching the
  // backend's count_completed_weeks. The raw checkins.length previously
  // counted out-of-window check-ins that settlement silently discards,
  // producing a progress figure that disagreed with the actual settlement.
  const weekCounts = inWindowWeekCounts(pledge);
  const inWindowVisits = Object.values(weekCounts).reduce((a, b) => a + b, 0);
  const completedWeeks = Object.values(weekCounts).filter(
    (n) => n >= pledge.visits_per_week
  ).length;
  const completedVisits = inWindowVisits;
  const progressPercentage = totalVisitsRequired > 0 ? (completedVisits / totalVisitsRequired) * 100 : 0;

  // Check-ins table columns - handle both string timestamps and object format.
  // When the backend provides a matching checkin_receipt (occurred_at +
  // server received_at), we also surface WHEN the server recorded it and a
  // "backdated" badge — the audit signal for the offline/background-fallback
  // flow, where a dwell can be delivered well after it occurred.
  const receiptByOccurredAt = {};
  (pledge.checkin_receipts || []).forEach((r) => {
    if (r && r.occurred_at) {
      // Key by epoch ms so minor ISO formatting differences still match.
      const t = new Date(r.occurred_at).getTime();
      if (!isNaN(t)) receiptByOccurredAt[t] = r;
    }
  });

  const findReceipt = (checkin) => {
    const ts = typeof checkin === 'string' ? checkin : checkin && checkin.timestamp;
    if (!ts) return null;
    const t = new Date(ts).getTime();
    return isNaN(t) ? null : (receiptByOccurredAt[t] || null);
  };

  // Human-readable delay between occurrence and server receipt.
  const formatDelay = (occurredAt, receivedAt) => {
    const a = new Date(occurredAt).getTime();
    const b = new Date(receivedAt).getTime();
    if (isNaN(a) || isNaN(b)) return null;
    const ms = b - a;
    if (ms < 5 * 60 * 1000) return null; // < 5 min: effectively real-time
    const mins = Math.round(ms / 60000);
    if (mins < 60) return `${mins}m`;
    const hrs = Math.round(mins / 60);
    if (hrs < 48) return `${hrs}h`;
    return `${Math.round(hrs / 24)}d`;
  };

  const checkinColumns = [
    { key: 'timestamp', header: 'Occurred At', render: (val, checkin) => {
      // If checkin is a string (just timestamp), use it directly
      const timestampValue = typeof checkin === 'string' ? checkin : val;
      if (!timestampValue) return 'N/A';
      try {
        const date = new Date(timestampValue);
        return isNaN(date.getTime()) ? `Raw: ${timestampValue}` : date.toLocaleString();
      } catch (error) {
        return `Raw: ${timestampValue}`;
      }
    }},
    { key: 'recorded_at', header: 'Recorded At', render: (val, checkin) => {
      const receipt = findReceipt(checkin);
      if (!receipt || !receipt.received_at) {
        // No receipt: either a legacy check-in (pre-audit) or admin-added.
        return <span className="text-gray-400">—</span>;
      }
      const recorded = new Date(receipt.received_at);
      const recordedStr = isNaN(recorded.getTime()) ? 'N/A' : recorded.toLocaleString();
      const occurredTs = typeof checkin === 'string' ? checkin : checkin.timestamp;
      const delay = formatDelay(occurredTs, receipt.received_at);
      return (
        <span className="text-sm text-gray-700">
          {recordedStr}
          {delay && (
            <span
              className="ml-2 inline-block bg-amber-100 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded"
              title="Recorded later than it occurred (offline / background delivery)"
            >
              +{delay} late
            </span>
          )}
        </span>
      );
    }},
    { key: 'actions', header: 'Actions', render: (val, checkin, index) => (
      <button
        onClick={() => handleDeleteCheckin(checkin)}
        className="bg-red-500 hover:bg-red-700 text-white text-xs px-2 py-1 rounded transition duration-150 ease-in-out"
        disabled={loading}
      >
        Delete
      </button>
    )}
  ];


  return (
    <div className="container mx-auto p-4 sm:p-6 lg:p-8">
      <div className="bg-white shadow-xl rounded-lg p-6 md:p-8">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 pb-4 border-b border-gray-200">
          <h1 className="text-3xl font-bold text-gray-800">Pledge Details (ID: {pledge.id})</h1>
          <button
            onClick={handleDeletePledge}
            disabled={loading || saving}
            className="mt-4 sm:mt-0 bg-red-500 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Delete Pledge'}
          </button>
        </div>

        {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4">{error}</p>}
        {successMessage && <p className="text-green-500 bg-green-100 p-3 rounded-md mb-4">{successMessage}</p>}

        {/* Pledge Info Display */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <InfoItem label="User Name" value={getUserNameById(pledge.user_id) || 'N/A'} />
          <InfoItem label="Charity Name" value={getCharityNameById(pledge.charity_id) || 'N/A'} />
          <InfoItem label="Original Amount" value={`€${(pledge.amount_cents / 100).toFixed(2)}`} />
          <InfoItem label="Recouped Amount" value={`€${(pledge.recouped_cents / 100).toFixed(2)}`} />
          <InfoItem label="Status" value={pledge.status} />
          <InfoItem label="Start Date" value={new Date(pledge.start_date).toLocaleDateString()} />
          <InfoItem label="End Date" value={new Date(pledge.end_date).toLocaleDateString()} />
          <InfoItem label="Duration" value={`${pledge.duration_weeks} weeks`} />
          <InfoItem label="Visits per Week" value={pledge.visits_per_week} />
          <InfoItem label="Target Location" value={
            typeof pledge.location_address === 'object' 
              ? `${pledge.location_address.street_address || ''} ${pledge.location_address.street_number || ''}, ${pledge.location_address.city || ''} ${pledge.location_address.post_code || ''}, ${pledge.location_address.country || ''}`.replace(/\s+/g, ' ').trim()
              : pledge.location_address
          } />
          <InfoItem label="Time Zone" value={pledge.time_zone || 'N/A'} />
          <InfoItem label="Created At" value={new Date(pledge.created_at).toLocaleString()} />
          <InfoItem label="Last Updated" value={new Date(pledge.updated_at).toLocaleString()} />
          {/* Stripe-funds-segregation correlation fields */}
          <InfoItem label="Payment Intent" value={pledge.payment_intent_id || 'N/A'} />
          <InfoItem label="Charge ID" value={pledge.charge_id || 'N/A'} />
          <InfoItem
            label="Total Charged"
            value={pledge.total_charged_cents != null ? `€${(pledge.total_charged_cents / 100).toFixed(2)}` : 'N/A'}
          />
          <InfoItem
            label="Stripe Fee"
            value={pledge.stripe_fee_cents != null ? `€${(pledge.stripe_fee_cents / 100).toFixed(2)}` : 'N/A'}
          />
          <InfoItem
            label="Platform Commission"
            value={pledge.platform_commission_cents != null ? `€${(pledge.platform_commission_cents / 100).toFixed(2)}` : 'N/A'}
          />
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Pledge Progress</h3>
          <ProgressBar percentage={progressPercentage} />
          <p className="text-sm text-gray-600 mt-1">
            {completedVisits} of {totalVisitsRequired} in-window visits completed
            ({completedWeeks} of {pledge.duration_weeks} weeks fully met).
          </p>
        </div>

        {/* Edit Form */}
        <form onSubmit={handleSubmit} className="mb-8 pt-6 border-t border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Edit Pledge</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-gray-700">Title</label>
              <input type="text" name="title" id="title" value={formData.title} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label htmlFor="amount_cents" className="block text-sm font-medium text-gray-700">Amount (Cents)</label>
              <input type="number" name="amount_cents" id="amount_cents" value={formData.amount_cents} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label htmlFor="duration_weeks" className="block text-sm font-medium text-gray-700">Duration (Weeks)</label>
              <input type="number" name="duration_weeks" id="duration_weeks" value={formData.duration_weeks} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label htmlFor="visits_per_week" className="block text-sm font-medium text-gray-700">Visits per Week</label>
              <input type="number" name="visits_per_week" id="visits_per_week" value={formData.visits_per_week} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
            </div>
            <div>
              <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">Start Date</label>
              <input type="date" name="start_date" id="start_date" value={formData.start_date} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm" />
              <p className="mt-1 text-xs text-gray-500">
                Backdate to simulate a completed pledge window. End date is
                recalculated as start + duration. Then add admin check-ins and settle.
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm text-gray-500">
            Pledge status is managed by the settlement flow (not editable here).
          </p>
          <div className="mt-8 flex justify-end">
            <button
              type="submit"
              disabled={saving || loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>

        {/* Add Check-in */}
        <div className="pt-6 border-t border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Add Check-in</h2>
          <div className="mb-8 bg-gray-50 p-6 rounded-lg">
            <p className="text-gray-600 mb-4">
              Select a date and add a check-in. The check-in is recorded at noon
              in the pledge's own timezone, so the date you pick is the date it
              counts for. Dates outside the pledge window are rejected.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end">
              <div className="flex-1">
                <label htmlFor="checkinDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Check-in Date{' '}
                  <span className="font-normal text-gray-500">
                    (pledge timezone: {pledge.time_zone || 'UTC'})
                  </span>
                </label>
                <input
                  type="date"
                  id="checkinDate"
                  value={selectedDate}
                  min={pledge.start_date ? String(pledge.start_date).split('T')[0] : undefined}
                  max={pledge.end_date ? String(pledge.end_date).split('T')[0] : undefined}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Valid range:{' '}
                  {pledge.start_date ? new Date(pledge.start_date).toLocaleDateString() : '?'}
                  {' '}–{' '}
                  {pledge.end_date ? new Date(pledge.end_date).toLocaleDateString() : '?'}
                  {' '}(inclusive).
                </p>
              </div>
              <button
                onClick={handleAddCheckin}
                disabled={addingCheckin || loading}
                className="bg-green-600 hover:bg-green-700 text-white font-bold py-2 px-6 rounded-lg transition duration-150 ease-in-out disabled:opacity-50"
              >
                {addingCheckin ? 'Adding Check-in...' : 'Add Check-in'}
              </button>
            </div>
          </div>
        </div>

        {/* Check-ins Table */}
        <div className="pt-6 border-t border-gray-200">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">Check-ins</h2>
          {(pledge.checkins && pledge.checkins.length > 0) ? (
            <div className="overflow-x-auto shadow-md rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 bg-white">
                <thead className="bg-gray-50">
                  <tr>
                    {checkinColumns.map(col => (
                      <th key={col.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {col.header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {pledge.checkins.map((checkin, index) => ( // Assuming checkin doesn't have its own ID, use index
                    <tr key={index} className="hover:bg-gray-50 transition-colors">
                      {checkinColumns.map(col => (
                        <td key={col.key} className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                          {col.render ? col.render(checkin[col.key], checkin, index) : (checkin[col.key] === undefined || checkin[col.key] === null ? 'N/A' : String(checkin[col.key]))}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-600">No check-ins recorded for this pledge.</p>
          )}
        </div>
      </div>
      <button
          onClick={() => navigate(-1)}
          className="mt-8 bg-gray-500 hover:bg-gray-600 text-white font-bold py-2 px-4 rounded-lg transition duration-150 ease-in-out"
        >
          Back
      </button>

      {showConfirmModal && (
        <ConfirmModal
          isOpen={showConfirmModal}
          title="Confirm Pledge Deletion"
          message={`Are you sure you want to delete pledge ID ${pledge.id}? This action cannot be undone.`}
          onConfirm={confirmDelete}
          onCancel={() => setShowConfirmModal(false)}
        />
      )}
      
      {showCheckinConfirmModal && (
        <ConfirmModal
          isOpen={showCheckinConfirmModal}
          title="Confirm Check-in Deletion"
          message={`Are you sure you want to delete this check-in? This action cannot be undone.`}
          onConfirm={confirmDeleteCheckin}
          onCancel={() => {
            setShowCheckinConfirmModal(false);
            setCheckinToDelete(null);
          }}
        />
      )}
      
      {showErrorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 transition-opacity duration-300 ease-in-out">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4 transform transition-all duration-300 ease-in-out scale-100">
            <h3 className="text-xl font-semibold text-red-600 mb-4">Error</h3>
            <p className="text-gray-700 mb-6">{errorModalMessage}</p>
            <div className="flex justify-end">
              <button
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const InfoItem = ({ label, value }) => (
  <div>
    <h3 className="text-sm font-medium text-gray-500">{label}</h3>
    <p className="mt-1 text-md text-gray-900">{value || 'N/A'}</p>
  </div>
);


export default PledgeView;
